import React, { useCallback, useMemo, useEffect, useState } from "react";
import { get } from "lodash";
import pluralize from "@tvg/utils/generalUtils";
import { useDispatch, useSelector } from "react-redux";
import uam from "@tvg/api/uam";
import formatCurrency from "@tvg/formatter/currency";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import BetUtils from "@tvg/utils/betSelection";
import {
  getBetType,
  getBetAmount,
  getBetGroupStatus,
  getProcessingBets,
  getSuccessBets,
  getErrorBets
} from "@tvg/desktop-bet/src/store/selectors";
import { setSelectionScratched } from "@tvg/desktop-bet/src/store/actions";
import { events as AlchemerEvents } from "@urp/alchemer";
import {
  AlertInline,
  breakpoints,
  Button,
  Checkbox,
  Header,
  Modal,
  Mtp,
  Paragraph,
  useColorTokens,
  useMediaQuery
} from "@tvg/design-system";
import {
  BetStatus,
  BetStatusType,
  SpecialGroupBets
} from "@tvg/desktop-bet/src/types";
import { getCurrentProbableAmount } from "@tvg/desktop-bet/src/utils/getWPSProbableValue";
import {
  filterRepeatedScratchedFromSelections,
  getBettingInfo
} from "@tvg/desktop-bet/src/utils/betUtils";
import { BetSummary } from "@tvg/desktop-bet/src/components/BetSummary";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { BetRepeatInfo } from "@tvg/ts-types/Bet";
import { isUserInAWFlows } from "@tvg/login-controller/src/selectors/user-ts";
import { getAccountNumber } from "@urp/store-selectors";
import {
  BetConfirmationContainer,
  ConfirmBetModalFooterWrapper
} from "./styled-components";
import { CONFIRM_MODAL_PREFERENCE, SavedBets } from "../../types";
import { setUserPrefs } from "../../store/actions";
import {
  getBetConfirmationMessages,
  getScratchedSelections,
  getShowBetConfirmPref,
  getBetConfirmModalTexts,
  getCancelLimitsBetSlipConf,
  getCancelLimitBetSlipEnabled,
  getHasRequestedLogin,
  getEnableUrpConfirmModalRepeatBet
} from "../../store/selectors";
import ConfirmModalHeader from "./components/ConfirmModalHeader";
import { useBetBlockedByState, usePlaceBet } from "../../hooks";
import { confirmBetGtmEvent } from "../../utils/gtm/betConfirmJourney";
import { useCancelLimits } from "./hooks/useCancelLimits";
import { useGeocomplyBetDisabled } from "./hooks/useGeocomplyBetDisabled";

interface ConfirmBetModalProps {
  race: RaceProgram;
  races: RaceProgram[];
  selections: string[][];
  isConfirmModalOpen: boolean;
  setConfirmModalOpen: (value: boolean) => void;
  behgClient: ApolloClient<NormalizedCacheObject>;
  onRepeatWager: (betBasicInfo: BetRepeatInfo) => void;
}

const ConfirmBetModal: React.FC<ConfirmBetModalProps> = ({
  race,
  races,
  selections,
  isConfirmModalOpen,
  setConfirmModalOpen,
  behgClient,
  onRepeatWager
}) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const colorTokens = useColorTokens();

  const dispatch = useDispatch();
  const enableAWFlows = useSelector(isUserInAWFlows);
  const {
    handleSubmitBet,
    resetBetting,
    allBetsPlaced,
    errorMessages,
    betDisable
  } = usePlaceBet();

  const selectedBetType = useSelector(getBetType);
  const accountNumber = useSelector(getAccountNumber);
  const accountShowBetConfirmPreference = useSelector(getShowBetConfirmPref);
  const betAmount = useSelector(getBetAmount);
  const betConfirmationMessages = useSelector(getBetConfirmationMessages);
  const betGroupStatus = useSelector(getBetGroupStatus);
  const isPlacingBet = useSelector(getProcessingBets);
  const isPlacingBetSuccess = useSelector(getSuccessBets);
  const isPlacingBetError = useSelector(getErrorBets);
  const scratchedSelections = useSelector(getScratchedSelections);
  const confirmModalMessages = useSelector(getBetConfirmModalTexts);
  const cancelLimitConfig = useSelector(getCancelLimitsBetSlipConf);
  const cancelLimitFeedbackEnabled = useSelector(getCancelLimitBetSlipEnabled);
  const hasRequestedLogin = useSelector(getHasRequestedLogin);
  const enableUrpConfirmModalRepeatBet = useSelector(
    getEnableUrpConfirmModalRepeatBet
  );

  const [isBlockedByState, blockedByStateErrorMessage] = useBetBlockedByState();
  const { isBetDisabled: isGeoComplyBetDisabled } = useGeocomplyBetDisabled();

  const [savedBets, setSavedBets] = useState<SavedBets>();
  const [retainOnClose, setRetainOnClose] = useState(false);
  const mtp = get(race, "mtp", "0");
  const trackName = get(race, "track.trackName");
  const raceNumber = get(race, "raceNumber");
  const postTime = get(race, "postTime");
  const trackCode = get(race, "track.trackCode");
  const trackCountry = get(race, "track.trackLocation.country");
  const status = get(race, "status.code");
  const placeBetError =
    isPlacingBetError > 0 && isPlacingBet === 0 && errorMessages.length;
  const isLoading = isPlacingBet > 0;
  const placeBetSuccess =
    isPlacingBetSuccess > 0 && isPlacingBet === 0 && !placeBetError;

  const typeFromRace = useMemo(
    () => BetUtils.getBetTypeFromRace(race, selectedBetType),
    [race.wagerTypes, selectedBetType]
  );

  const {
    fullSelections,
    isSpecialGroup,
    specialGroupBets,
    totalAmount,
    numberOfWagerableRunners
  } = getBettingInfo(selections, betAmount, race, races, typeFromRace);

  const placeBetNotAllowed =
    isBlockedByState ||
    betDisable ||
    isGeoComplyBetDisabled ||
    (Number(totalAmount) === 0 && isConfirmModalOpen) ||
    !race.wagerable ||
    placeBetSuccess;

  const errorButtonTypes = useMemo(
    () => errorMessages.map((error) => error.message.buttonType),
    [errorMessages]
  );

  const filteredScratches = filterRepeatedScratchedFromSelections(
    savedBets?.fullSelections || [],
    scratchedSelections
  );

  const hasScratches = filteredScratches?.some(
    (scratches: string[]) => scratches?.length > 0
  );

  const currentWagerType = useMemo(
    () =>
      race.wagerTypes?.find(
        (wagerType) => wagerType.type.id === selectedBetType
      ),
    [race.wagerTypes]
  );

  useEffect(() => {
    if (allBetsPlaced) {
      AlchemerEvents.placeABet();
    }
  }, [allBetsPlaced]);

  const approxPayout = getCurrentProbableAmount(
    get(race, "probables", []),
    betAmount,
    selections,
    isSpecialGroup,
    currentWagerType
  );

  const resetBettingAndCloseModal = () => {
    setRetainOnClose(false);
    setConfirmModalOpen(false);
    const scratches = BetUtils.setMatrixSizeByColumnCount(
      get(typeFromRace, "columnCount", 1)
    );
    dispatch(setSelectionScratched(scratches));
  };

  const retainRunnersAndCloseModal = () => {
    setRetainOnClose(true);
    setConfirmModalOpen(false);
  };

  const handleAfterClose = () => {
    resetBetting(retainOnClose);
  };

  const onConfirmBetModal = () => {
    handleSubmitBet({
      betAmount,
      betSelectionsGroup: fullSelections,
      race
    });
    confirmBetGtmEvent({
      raceNumber,
      trackName,
      betType: selectedBetType,
      betAmount: Math.trunc(+totalAmount)
    });
  };

  useEffect(() => {
    if (
      isConfirmModalOpen &&
      ((!hasRequestedLogin && !enableAWFlows) ||
        accountShowBetConfirmPreference === CONFIRM_MODAL_PREFERENCE.NEVER)
    ) {
      onConfirmBetModal();
    }
  }, [isConfirmModalOpen]);

  const updateBetConfirmationPreference = useCallback(
    async (value: string[]) => {
      let show_bet_confirm = CONFIRM_MODAL_PREFERENCE.ALWAYS;

      if (value.length && value[0] === "bet-confirmation") {
        show_bet_confirm = CONFIRM_MODAL_PREFERENCE.NEVER;
      }

      try {
        await uam.setUserPref(
          accountNumber,
          "show_bet_confirm",
          show_bet_confirm
        );
      } catch (error) {
        console.error(
          "Something happend while trying to save user preference: ",
          error
        );
      }

      dispatch(
        setUserPrefs(
          {
            show_bet_confirm
          },
          true
        )
      );
    },
    [accountNumber]
  );

  const numberOfBets =
    isSpecialGroup && specialGroupBets
      ? Object.keys(specialGroupBets).filter(
          (bet) => specialGroupBets[bet].length
        ).length
      : 1;

  useEffect(() => {
    if (isConfirmModalOpen) {
      setSavedBets({
        ...savedBets,
        betAmount,
        betNumber: numberOfBets,
        approxPayout,
        totalAmount,
        wagerableRunners: numberOfWagerableRunners,
        betProbables: get(race, "probables", []),
        betSelectionsNumbers: selections,
        fullSelections: fullSelections.map((runnerSelections) =>
          runnerSelections.filter((runner) => !runner.scratched)
        ),
        specialGroupBets: (() => {
          const groupBets: SpecialGroupBets = {};
          Object.keys(specialGroupBets || {}).forEach((key) => {
            groupBets[key] = (specialGroupBets || {})[key]?.filter(
              (runner) => !runner.scratched
            );
          });
          return groupBets;
        })()
      });
    }
  }, [isConfirmModalOpen]);

  const getNumOfBets =
    placeBetNotAllowed && savedBets ? savedBets.betNumber : numberOfBets;

  const betsStatus = Object.values<BetStatus>(betGroupStatus).map(
    (betType) => betType && betType.status
  );

  const getNumberOfWagerableRunners = () =>
    placeBetNotAllowed ? savedBets?.wagerableRunners : numberOfWagerableRunners;

  const showRaceOffAlert =
    (status === RaceStatusEnum.RACE_OFF &&
      Object.keys(betGroupStatus).length !== 0 &&
      betsStatus.includes(BetStatusType.ERROR)) ||
    (status === RaceStatusEnum.RACE_OFF &&
      Object.keys(betGroupStatus).length === 0);

  const { feedback } = useCancelLimits({
    needsFetch: isConfirmModalOpen && cancelLimitFeedbackEnabled,
    accountNumber,
    behgClient,
    config: cancelLimitConfig,
    feedbackExtraValues: {
      forceFeedback: placeBetSuccess ? "placed" : undefined,
      prediction: {
        amount: betAmount,
        count: numberOfBets
      },
      track: {
        code: trackCode,
        name: trackName,
        country: trackCountry
      }
    },
    currentRace: trackCode
  });

  const repeatWager = (betRepeatInfo?: BetRepeatInfo) => {
    onRepeatWager({
      wagerType: betRepeatInfo?.wagerType || typeFromRace.type,
      wagerAmount: betRepeatInfo?.wagerAmount || betAmount,
      selections: {
        selection:
          betRepeatInfo?.selections?.selection ||
          selections.reduce(
            (acc: BetRepeatInfo["selections"]["selection"] | [], cur) => {
              acc.push(
                // @ts-ignore
                cur.reduce(
                  (acc2: { runners: Array<{ number: number }> | [] }, cur2) => {
                    // @ts-ignore
                    acc2.runners.push({ number: Number(cur2) });
                    return acc2;
                  },
                  { runners: [] }
                )
              );
              return acc;
            },
            []
          )
      }
    });
  };

  return (
    <Modal
      isOpen={isConfirmModalOpen}
      title={
        placeBetSuccess ? (
          <ConfirmModalHeader
            title={
              isSpecialGroup || specialGroupBets
                ? "All Bets Placed"
                : "Bet Placed"
            }
          />
        ) : (
          "Confirm Bet"
        )
      }
      type={isMobile ? "flexible" : "lightbox"}
      padding={0}
      onClose={resetBettingAndCloseModal}
      onOverlayClick={resetBettingAndCloseModal}
      hasBorderBottom={race.wagerable}
      shouldCloseOnEsc={false}
      qaLabel="confirm-bet-modal"
      onAfterClose={handleAfterClose}
    >
      <>
        {!race.wagerable && showRaceOffAlert && (
          <AlertInline
            type="fixed"
            qaLabel="raceOffErrorConfirmBet"
            message={
              betConfirmationMessages?.noLongerAvailableForBetting ||
              "The race has started and you can no longer bet"
            }
            variant="warning"
          />
        )}
        {race.wagerable && hasScratches && (
          <AlertInline
            type="fixed"
            qaLabel="raceOffErrorConfirmBet"
            title={
              Number(totalAmount) > 0
                ? confirmModalMessages?.validBet
                : confirmModalMessages?.invalidBet
            }
            message=""
            variant="warning"
          />
        )}
        {!!placeBetError &&
          errorMessages.map((errorMessage) => (
            <AlertInline
              type="fixed"
              variant="error"
              key={errorMessage.code}
              title={
                errorMessage.message.title
                  ? errorMessage.message.title
                  : errorMessage.message.text
              }
              message={
                errorMessage.message.title ? errorMessage.message.text : ""
              }
              qaLabel="alert-inline"
              mb="space-4"
            />
          ))}
        {isBlockedByState && (
          <AlertInline
            type="fixed"
            variant="error"
            message={blockedByStateErrorMessage}
            qaLabel="state-blocked-error"
            mb="space-4"
          />
        )}
        {feedback && (
          <AlertInline
            type="fixed"
            qaLabel="raceCancelLimits"
            title={feedback.title}
            message={feedback.description || ""}
            variant="informational"
            actionLink={
              feedback.link && (
                <AlertInline.Link
                  qaLabel="alert-inline-link"
                  href={feedback.link.url}
                >
                  {feedback.link.label}
                </AlertInline.Link>
              )
            }
          />
        )}
        <BetConfirmationContainer>
          <Mtp
            isCollapsed
            mtp={+mtp}
            status={status}
            postTime={postTime as string}
            hasFullText
            qaLabel="confirm-bet-modal-mtp"
          />
          <Header
            tag="h3"
            fontFamily="bold"
            color={colorTokens.content.default}
            qaLabel="confirm-bet-modal-race-name"
            mb="space-1"
          >
            {`${trackName} R${raceNumber}`}
          </Header>
          <Paragraph
            color={colorTokens.content.subtle}
            qaLabel="confirm-bet-modal-bet-description"
            mb="space-4"
          >{`${pluralize(getNumOfBets, "Bet")} • ${formatCurrency(
            placeBetNotAllowed && savedBets?.totalAmount
              ? savedBets.totalAmount
              : totalAmount
          )} Staked`}</Paragraph>
          {
            // This component will render 1 bet summary if betType is not marked as special bet and multiple if special eg: WPS
          }
          <BetSummary
            numWagerableRunners={getNumberOfWagerableRunners()}
            betSelectionsNumbers={
              placeBetNotAllowed
                ? savedBets?.betSelectionsNumbers || []
                : selections
            }
            betAmount={placeBetNotAllowed ? savedBets?.betAmount : betAmount}
            fullSelections={savedBets?.fullSelections || []}
            approxPayout={
              placeBetNotAllowed ? savedBets?.approxPayout : approxPayout
            }
            betTotal={
              placeBetNotAllowed ? savedBets?.totalAmount || "0" : totalAmount
            }
            specialGroupBets={savedBets?.specialGroupBets}
            // @ts-ignore
            specialGroupBetsWithOutScratches={
              placeBetNotAllowed
                ? savedBets?.specialGroupBets
                : specialGroupBets
            }
            isSpecialGroup={isSpecialGroup}
            typeFromRace={typeFromRace}
            betGroupStatus={betGroupStatus}
            allBetsPlaced={allBetsPlaced}
            placeBetSuccess={placeBetSuccess}
            probables={
              placeBetNotAllowed
                ? savedBets?.betProbables || []
                : get(race, "probables", [])
            }
            raceNumber={raceNumber}
            scratches={filteredScratches || [[]]}
            bettingInterests={race.bettingInterests}
            {...(enableUrpConfirmModalRepeatBet
              ? { onRepeatWager: repeatWager }
              : {})}
          />
          {race.wagerable && Number(totalAmount) > 0 ? (
            <ConfirmBetModalFooterWrapper>
              {!placeBetSuccess ? (
                <Button
                  variant="betting"
                  onPress={onConfirmBetModal}
                  qaLabel="confirm-bet-modal-button"
                  mb="space-4"
                  isStretched
                  isLoading={isLoading}
                  isDisabled={placeBetNotAllowed}
                >
                  {`${
                    errorButtonTypes.includes("retry") ? "Retry" : "Confirm"
                  } ${formatCurrency(totalAmount)} bet`}
                </Button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    onPress={resetBettingAndCloseModal}
                    qaLabel="confirm-bet-modal-sucess-button"
                    mb="space-4"
                    isStretched
                    // is Loading place bet
                    isLoading={isLoading}
                  >
                    Return to Race
                  </Button>
                  <Button
                    variant="secondary"
                    onPress={retainRunnersAndCloseModal}
                    qaLabel="confirm-bet-modal-retain-button"
                    mb="space-4"
                    isStretched
                    // is Loading place bet
                    isLoading={isLoading}
                  >
                    Retain Runners
                  </Button>
                </>
              )}
              {!!placeBetError &&
                (betDisable
                  ? !errorButtonTypes.includes("retry")
                  : errorButtonTypes.includes("close")) && (
                  <Button
                    variant="secondary"
                    onPress={resetBettingAndCloseModal}
                    qaLabel="confirm-bet-modal-close-button"
                    mb="space-4"
                    isStretched
                  >
                    Close
                  </Button>
                )}
              {!placeBetSuccess && (
                <Checkbox.Group
                  selectedValues={
                    accountShowBetConfirmPreference ===
                    CONFIRM_MODAL_PREFERENCE.NEVER
                      ? ["bet-confirmation"]
                      : []
                  }
                  onChange={updateBetConfirmationPreference}
                  qaLabel="bet-confirmation-checkbox-group"
                >
                  <Checkbox
                    value="bet-confirmation"
                    label="Don't show me confirmation step again"
                    qaLabel="bet-confirmation-checkbox"
                    mb={0}
                    alignLabel="start"
                  />
                </Checkbox.Group>
              )}
            </ConfirmBetModalFooterWrapper>
          ) : (
            <Button
              variant="secondary"
              onPress={resetBettingAndCloseModal}
              qaLabel="confirm-bet-modal-close-button"
              mb="space-4"
              isStretched
            >
              Close
            </Button>
          )}
        </BetConfirmationContainer>
      </>
    </Modal>
  );
};

export default ConfirmBetModal;
